import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as api from './marketing-tags.api';

import type {
  CreateWebsiteMarketingTagRequest,
  UpdateWebsiteMarketingTagRequest,
  WebsiteMarketingTagListQuery,
} from '@utazzo/shared';

const QK = {
  list: (query: WebsiteMarketingTagListQuery) => ['marketing-tags', 'list', query] as const,
  detail: (id: string) => ['marketing-tags', 'detail', id] as const,
};

export const useMarketingTagsList = (query: WebsiteMarketingTagListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => api.listMarketingTags(query),
    staleTime: 30_000,
  });

export const useCreateMarketingTag = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateWebsiteMarketingTagRequest) => api.createMarketingTag(payload),
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: ['marketing-tags', 'list'] });
      toast.success(`Added ${t.name}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add tag'),
  });
};

export const useUpdateMarketingTag = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateWebsiteMarketingTagRequest }) =>
      api.updateMarketingTag(id, payload),
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: ['marketing-tags', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(t.id) });
      toast.success('Tag updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update tag'),
  });
};

export const useDeleteMarketingTag = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteMarketingTag(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['marketing-tags', 'list'] });
      toast.success('Tag deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete tag'),
  });
};

export const useBulkDeleteMarketingTags = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => api.bulkDeleteMarketingTags(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['marketing-tags', 'list'] }),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete tags'),
  });
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
